import React, { useState, useEffect, useMemo, useRef } from 'react';
import './../../App.css'; // Assuming you have a CSS file for styling
import './AnimePage.css'; // Assuming you have a CSS file for styling
import { useParams, useNavigate } from 'react-router-dom';

// Import logo.svg from the assets folder
import Header from '../../components/Header';
import PlayIcon from '../../assets/ic_play.svg';
import NotifyIcon from '../../assets/ic_notification.svg';
import NoNotifyIcon from '../../assets/ic_nonotification.svg';
import Footer from '../../components/Footer';
import useScrolled from '../../hooks/useScrolled';
import { DetailSkeleton } from '../../components/layout/Skeletons';
import video from './../../assets/output.mp4';
import AnimeCardComponent from '../../components/animecard/AnimeCardComponent';
import CustomDropdown from '../../components/controls/CustomDropdown';
import infoIcon from '../../assets/ic_info.svg';
import ApiService from '../../services/ApiService';
import KodikApiService from '../../services/KodikApiService';

import SeasonComponent from '../../components/SeasonComponents';
import { flattenEpisodeSources, mergeEpisodeSources } from '../../components/player/episodeSources';
import LeaveReviewComponent from '../../components/LeaveReviewComponent';
import ReviewComponent from '../../components/ReviewComponent';
import YandexAdComponent from '../../components/YandexAdComponent';
import helpIcon from '../../assets/help.jpg';
import eyeIcon from '../../assets/ic_eye.svg';
import genStar from '../../assets/genStar.svg';
import AppPromo from '../../components/AppPromo';
const AnimePage = () => {
    const { id } = useParams();
    const scrolled = useScrolled();
    const [anime, setAnime] = useState([]);
    const [seriesData, setSeriesData] = useState(null);
    const [kodikPlayback, setKodikPlayback] = useState(null);
    const [url, setUrl] = useState(video);
    const [leaveReviewPopup, setLeaveReviewPopup] = useState(false);

    const videoRef = useRef(null);
    const navigate = useNavigate();
    const [seriesCount, setSeriesCount] = useState(0);
    const [collections, setCollections] = useState(null);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [showLoadReviewsButton, setShowLoadReviewsButton] = useState(true);
    const [reviewsPage, setReviewsPage] = useState(0);
    const [isUnavailable, setIsUnavailable] = useState(false);
    const [unavailableReason, setUnavailableReason] = useState("Контент недоступен для просмотра для зрителей из всего мира. Возможно, мы проводим временные работы с сериями этого аниме");
    const [reviews, setReviews] = useState([]);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [resumeSnippet, setResumeSnippet] = useState(null);
    const hasSession = !!localStorage.getItem('sessionKey');
    const combinedSeriesData = useMemo(
        () => mergeEpisodeSources(seriesData, kodikPlayback, id),
        [id, kodikPlayback, seriesData]
    );
    const firstSeries = useMemo(
        () => flattenEpisodeSources(combinedSeriesData)[0] || null,
        [combinedSeriesData]
    );


    // New flag to track if data has been fetched
    const hasFetched = useRef(false);

    useEffect(() => {
        // Scroll to the top of the page when the component is rendered
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (seriesData == null && kodikPlayback == null) return;
        let count = 0;
        Object.values(combinedSeriesData).forEach(season => {
            count += season.length;
        });
        setSeriesCount(count);
    }, [combinedSeriesData, kodikPlayback, seriesData]);

    const fetchData = async () => {
        const blacklistedData = await ApiService.isBlacklisted(id);

        if (blacklistedData.data == true && localStorage.getItem("app") !== "true") {
            setIsUnavailable(true)
            setUnavailableReason("Недоступно для просмотра согласно действующему законодательству Вашей страны")


            return
        }
        const animeData = await ApiService.getAnime(id);
        try {
            if (animeData.type === "success") {
                setAnime(animeData.data);
                if (localStorage.getItem("isAdult") == null) {
                    setIsPopupOpen(animeData.data.pg >= 18);
                }
                document.title = animeData.data.name + " на animix";
            } else {
                setIsUnavailable(true);
                return;
            }
        } catch (error) {
            console.error('Error fetching anime list:', error);
        }

        const [animixSeriesResult, kodikPlaybackResult] = await Promise.allSettled([
            ApiService.getSeries(id),
            KodikApiService.getPlaybackSources(id),
        ]);

        if (animixSeriesResult.status === 'fulfilled') {
            const payload = animixSeriesResult.value;
            setSeriesData(payload?.type === 'success' && payload?.data ? payload.data : {});
        } else {
            console.error('Error fetching series data:', animixSeriesResult.reason);
            setSeriesData({});
        }

        if (kodikPlaybackResult.status === 'fulfilled') {
            setKodikPlayback(kodikPlaybackResult.value);
        } else if (!['ANIME_NOT_FOUND', 'PLAYER_NOT_FOUND', 'MATCH_AMBIGUOUS'].includes(kodikPlaybackResult.reason?.code)) {
            console.error('Error fetching Kodik playback sources:', kodikPlaybackResult.reason);
        }

        fetchReviews(id, 0);

        try {
            fetchCollections();

        } catch (error) {
            console.error('Error fetching collections:', error);
        }

        // Try to fetch recent watches and find a resume candidate for this anime
        try {
            if (localStorage.getItem('sessionKey')) {
                const last = await ApiService.getLastWatch();
                const list = Array.isArray(last?.data) ? last.data : [];
                const animeIdNum = Number(id);
                const resume = list.find(w => w.animeId === animeIdNum && !w.watched);
                if (resume) setResumeSnippet(resume);
            }
        } catch (e) {
            // Ignore resume errors silently
        }

    };

    const fetchCollections = async () => {
        try {
            const profileId = localStorage.getItem("profileId");
            const collectionsData = await ApiService.getCollections(profileId);

            collectionsData.data.forEach(element => {
                const animeId = parseInt(id);
                if (element.animeIds.includes(animeId)) {
                    setSelectedCollection(element.id);
                }
            });
            setCollections(collectionsData.data);
        } catch (error) {
            console.error('Error fetching collections:', error);
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            fetchData();
            hasFetched.current = true; // Mark data as fetched
        }
    }, []);


    const handleImageLoad = () => {
        setImageLoaded(true);
    };



    const handleWatchButton = () => {
        if (!hasSession) {
            navigate('/login');
            return;
        }
        if (firstSeries) navigate(`/watch/${anime.id}/${firstSeries.id}`);
    };

    const handleLoad = () => {
        if (videoRef.current != null) {
            if (videoRef.current.currentTime < 50) {
                videoRef.current.currentTime = 50;
            }
        }
    };


    const handleSubscribeToggle = async (e) => {
        const data = await ApiService.setSubscribed(!anime.subscribed, anime.id);
        fetchData();
    };

    const handleStatusChange = async (newValue) => {
        await ApiService.addCollection(newValue, anime.id);
        setSelectedCollection(newValue);
        fetchCollections();
    };

    const fetchReviews = async (animeId, page) => {
        try {
            const reviewsData = await ApiService.getReviews(animeId, page);

            if (page === 0) {
                setReviews(reviewsData.data);
            } else {
                setReviews(reviews.concat(reviewsData.data));
            }

            if (reviewsData.data.length < 5) {
                setShowLoadReviewsButton(false);
            }
        } catch (error) {
            console.error('Error fetching reviews:', error);
        }
    };

    function formatWatchCount(num) {
        if (num >= 10000000) {
            return (num / 10000000).toFixed(2) + "Cr"; // Crores (1Cr = 10 million)
        } else if (num >= 1000000) {
            return (num / 1000000).toFixed(2) + "M"; // Millions
        } else if (num >= 1000) {
            return (num / 1000).toFixed(2) + "K"; // Thousands
        }
        return num; // Less than 1000, return as-is
    }

    const API_DOMAIN = 'https://api.whitewhale.help';
    const scrollToSection = (sectionId) => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="app anime-page">
            <Header scrolled={scrolled} />

            {isUnavailable ? (
                <div className='content unavailable-con'>
                    <img src={helpIcon} />
                    <h2>Это аниме недоступно</h2>
                    <p>{unavailableReason}</p>
                </div>
            ) : !anime?.id ? (
                <DetailSkeleton />
            ) : (
                <div className="ui-enter">
                    <div className="anime-info-content">
                        {/* <video src={url} type="video/mp4" ref={videoRef} onPlay={handleLoad} className="backgroundVideo" autoPlay loop muted /> */}
                        <img
                            src={`${API_DOMAIN}/anime/getThumb/${anime.id}_thumb.png`}
                            alt={anime.name}
                            className={`blud-back ${imageLoaded ? 'loaded' : ''}`}
                            onLoad={handleImageLoad}
                        />
                        <div className='anime-info-container'>

                            <AnimeCardComponent anime={anime} />
                            <div className='anime-info-div'>
                                <div className='center-content'>
                                    <p className={`pg-anime-text ${anime.pg == 18 ? "adult" : ""}`}>{anime.pg}+</p>

                                    {anime.hq ? <p className={`pg-anime-text hiq`}>Высокое качество</p> : ''}
                                    {anime.viral > 10 ? <p className={`pg-anime-text viral`}>Популярность {anime.viral}</p> : ''}
                                    {anime.studio != null ? <p className={`pg-anime-text studio`}>{anime.studio}</p> : ''}
                                </div>
                               
                                <h1 className='anime-name'>{anime.name}</h1>

                                <p className='anime-text'>Выпуск: {anime.productionDates}<br />

                                    Жанр: {anime.genres}</p>

                                <div className='anime-actions-con'>
                                    {firstSeries ? (
                                        <button className='actionButton playButton2' onClick={handleWatchButton}>
                                            <img src={PlayIcon} alt="Play Icon" className="playIcon" />
                                            {hasSession ? (anime.movie ? "Смотреть" : `${firstSeries.seriesNum} серия`) : "Войти и смотреть"}
                                        </button>
                                    ) : ""}
                                    <button className='actionButton subscribeButton' onClick={handleSubscribeToggle}>  <img src={anime.subscribed ? NoNotifyIcon : NotifyIcon } alt="Play Icon" className="playIcon" /></button>
                                    {localStorage.getItem("profileId") == null ? "" :
                                        <div>

                                            <CustomDropdown
                                                placeholder="Не выбрано"
                                                value={selectedCollection ?? -1}
                                                onChange={handleStatusChange}
                                                options={[
                                                    { value: -1, label: 'Не выбрано' },
                                                    ...(collections ? collections.map(c => ({ value: c.id, label: c.name })) : [])
                                                ]}
                                            />
                                        </div>
                                    }

                                    <div className="watch-count-con">
                                        <img src={eyeIcon} />
                                        <p>{formatWatchCount(anime.watchCount)}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <nav className="mobile-anime-tabs" aria-label="Разделы страницы">
                        <button type="button" onClick={() => scrollToSection('anime-about')}>Обзор</button>
                        {hasSession ? (
                            <button type="button" onClick={() => scrollToSection('anime-episodes')}>Серии</button>
                        ) : null}
                        <button type="button" onClick={() => scrollToSection('anime-reviews')}>Отзывы</button>
                    </nav>
                    <div className={`anime-half-con${hasSession ? '' : ' catalog'}`}>
                        <div className="anime-half-desc" id="anime-about">

                            {anime.announce != null ? anime.announce.length > 0 ? <div className='announce'>
                                <img src={infoIcon} className='profile-card-icon'></img>
                                <a>{anime.announce}</a></div> : "" : ""}


                            <h2 className="anime-section-title">О чём</h2>
                            <div className="anime-text-small-container">
                                <p className={`anime-text-small-desc ${isDescriptionExpanded ? 'expanded' : ''}`}>
                                    {anime.description}
                                </p>
                                {!isDescriptionExpanded && (
                                    <button
                                        type="button"
                                        className="expand-button"
                                        onClick={() => setIsDescriptionExpanded(true)}
                                    >
                                        Показать полностью
                                    </button>
                                )}
                            </div>
                            <h2 className="anime-section-title" id="anime-reviews">Оценка пользователей</h2>
                            {anime.userRating > 0 ? <div>  <div className="general-stars-con">
                                <img src={genStar} alt="star icon" className="star-icon" />
                                <h1>{anime.userRating}</h1>
                            </div>
                                <p className='settings-text-small'>Средняя оценка среди пользователей animix. {anime.rating} - рейтинг animix</p></div>
                                : ''}

                            <button type="button" className='ui-btn ui-btn--secondary anime-review-cta' onClick={() => { setLeaveReviewPopup(true) }}>Оставить отзыв</button>

                            {leaveReviewPopup == true ?
                                <div className="review-popup-overlay" onClick={() => { setLeaveReviewPopup(false) }}>
                                    <div className="review-popup-content" onClick={(e) => e.stopPropagation()}>
                                        <h2>Ваш отзыв</h2>
                                        <LeaveReviewComponent animeId={anime.id} onSubmit={() => { fetchReviews(id, 0); setLeaveReviewPopup(false); }} />
                                    </div>
                                </div>
                                : ''
                            }



                            <div className="reviews">
                                {reviews.length !== 0 ? reviews.map((review) => (
                                    <ReviewComponent key={review.id} review={review} />
                                )) : ""}
                            </div>
                            {showLoadReviewsButton ? <button type="button" className='ui-btn ui-btn--secondary anime-load-more' onClick={() => {
                                setReviewsPage(reviewsPage + 1);
                                fetchReviews(id, reviewsPage + 1);
                            }}>Загрузить ещё</button> : ""}
                        </div>
                        {hasSession ? (
                            <div className="anime-half" id="anime-episodes">
                                {localStorage.getItem("subscribed") == true ? "" : <YandexAdComponent blockId="R-A-8131234-2" />}

                                {resumeSnippet && (
                                    (() => {
                                        const API_DOMAIN = 'https://api.whitewhale.help';
                                        return (
                                            <div className='now-watching' onClick={() => navigate(`/watch/${resumeSnippet.animeId}/${resumeSnippet.seriesId}`)}>
                                                <img
                                                    className='thumb'
                                                    src={`${API_DOMAIN}/anime/getThumb/${resumeSnippet.privateVideoId}_thumb.png`}
                                                    alt={resumeSnippet.animeName}
                                                />
                                                <div className='info'>
                                                    <span className='pill'>Продолжить</span>
                                                    <span className='title'>{resumeSnippet.animeName}</span>
                                                    <span className='meta'>{resumeSnippet.series} серия, {resumeSnippet.season} сезон</span>
                                                </div>
                                            </div>
                                        );
                                    })()
                                )}

                                <h2 className="anime-section-title">{seriesData != null || kodikPlayback != null ? seriesCount === 1 ? "Смотреть" : "Смотреть серии" : "Смотреть серии"}</h2>
                                {seriesData != null || kodikPlayback != null ? <SeasonComponent seriesData={combinedSeriesData} animeData={anime} canWatch={hasSession} /> : <p>Загружаем серии...</p>}
                            </div>
                        ) : null}
                    </div>

                    {isPopupOpen && (
                        <div className="popup-container">
                            <div className="popup" onClick={() => setIsPopupOpen(false)}>
                                <h2 className='popup-title'>Вам есть 18 лет?</h2>
                                <p>Это произведение предназначено только для взрослой аудитории</p>
                                <button onClick={() => {
                                    if (localStorage.getItem("profileId") != null) {
                                        localStorage.setItem("isAdult", true);
                                    }
                                    setIsPopupOpen(false);
                                }}>Да, больше не показывать</button>
                                <button className='button-sec' onClick={() => navigate('/')}>Нет</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <AppPromo />
            <Footer />
        </div>
    );
};

export default AnimePage;
